// Shared helpers used across the Kyte provider modules.

import { is_adult, is_infant } from '@liveflight/utilities/travelers'

/**
 * Unwrap a Kyte service_handler response. The salabam lfk_*.php proxy
 * (and the local api-testing-server) wrap Kyte payloads as
 * `{success, data}`; service_handler doesn't itself reject on
 * `success:false`, so unwrap throws to surface upstream failures
 * instead of silently returning a partial body.
 * @param {object} response
 * @returns {object}
 */
export const unwrap = (response) => {
  if (!response) {
    return response
  }
  if (response.success === false) {
    throw new Error('[kyte] upstream returned success:false')
  }
  return response.data
}

/**
 * Every non-infant travelerId on a canonical FlightOffer. Infants are excluded
 * because they don't get their own seat or checked-bag slot.
 * @param {object} flight_offer
 * @returns {string[]}
 */
export const collect_traveler_ids = (flight_offer) => {
  const { travelerPricings = [] } = flight_offer || {}
  return travelerPricings
    .filter((p) => !is_infant(p))
    .map((p) => p.travelerId)
}

/**
 * Pair each unlinked infant with the next adult no other infant has claimed,
 * in travelerId order. Kyte never populates `associatedAdultId` (BookFlight
 * rejects it as "not allowed"), so the link is local-only — synthesized on the
 * FlightOffer for the seat picker, sidebar, and traveler form. Idempotent.
 * @param {Array} traveler_pricings
 * @returns {Array}
 */
export const ensure_infant_associations = (traveler_pricings) => {
  const list = Array.isArray(traveler_pricings) ? traveler_pricings : []
  const has_unlinked_infant = list.some(
    (tp) => is_infant(tp) && !tp.associatedAdultId
  )
  if (!has_unlinked_infant) return list

  const adult_ids = list.filter(is_adult).map((tp) => tp.travelerId)
  const claimed = new Set(
    list
      .filter((tp) => is_infant(tp) && tp.associatedAdultId)
      .map((tp) => tp.associatedAdultId)
  )
  const free_adults = adult_ids.filter((id) => !claimed.has(id))

  return list.map((tp) => {
    if (!is_infant(tp) || tp.associatedAdultId) return tp
    const next = free_adults.shift()
    if (!next) return tp
    return { ...tp, associatedAdultId: next }
  })
}
